import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview ChronoGroup — LOCI chrono itinerary section.
 *
 * Eyebrow row with a chrono label + optional date range on the left and a mono
 * `{count} saved` coord on the right, followed by a list of event rows.
 * Used inside the Saved itinerary surface (FRE-769) to bucket events into
 * Tonight / This Weekend / Next Week / Later groups.
 */
import React, { forwardRef } from 'react';
import { useTheme } from '../../../theme';
import { Eyebrow } from '../Eyebrow';
import { Coord } from '../Coord';
/**
 * `<ChronoGroup>` — chrono itinerary section composed of an eyebrow + saved
 * count header and a vertical stack of event rows.
 *
 * Returns `null` when both `children` and `emptyState` resolve to nothing —
 * empty groups are hidden per the FRE-769 acceptance criteria. Pass an
 * `emptyState` to keep the layout slot visible while still acknowledging
 * "no events in this bucket."
 */
export const ChronoGroup = forwardRef(({ label, dateRange, count, emptyState, children, className, style, 'data-testid': dataTestId = 'chrono-group', }, ref) => {
    const theme = useTheme();
    const visibleChildren = React.Children.toArray(children).filter(Boolean);
    const hasChildren = visibleChildren.length > 0;
    // Treat `undefined`, `null`, and `false` as "no fallback provided" so
    // callers can conditionally render via `cond && <Foo />` without leaking
    // an empty section (FRE-769 AC).
    const hasRenderableEmptyState = emptyState !== undefined && emptyState !== null && emptyState !== false;
    if (!hasChildren && !hasRenderableEmptyState) {
        return null;
    }
    const sectionStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.md,
    };
    const headerStyle = {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: theme.spacing.sm,
    };
    const labelGroupStyle = {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: theme.spacing.sm,
        minWidth: 0,
    };
    const listStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.xs,
        listStyle: 'none',
        margin: 0,
        padding: 0,
    };
    const emptyStyle = {
        color: theme.colors.textMuted,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        padding: `${theme.spacing.md} 0`,
    };
    const ariaLabel = dateRange
        ? `${label} ${dateRange} — ${count} saved`
        : `${label} — ${count} saved`;
    return (_jsxs("section", { ref: ref, className: className, style: { ...sectionStyle, ...style }, "aria-label": ariaLabel, "data-testid": dataTestId, "data-chrono-label": label, children: [_jsxs("header", { style: headerStyle, "data-testid": "chrono-group-header", children: [_jsxs("span", { style: labelGroupStyle, children: [_jsx(Eyebrow, { children: label }), dateRange && (_jsx(Coord, { size: "xs", "aria-hidden": "true", children: dateRange }))] }), _jsxs(Coord, { size: "xs", "data-testid": "chrono-group-count", children: [count, " saved"] })] }), hasChildren ? (_jsx("ul", { style: listStyle, "data-testid": "chrono-group-list", children: visibleChildren.map((child, index) => {
                    const element = child;
                    const childKey = element?.key ?? index;
                    return (_jsx("li", { role: "none", style: { listStyle: 'none' }, children: child }, childKey));
                }) })) : hasRenderableEmptyState ? (_jsx("div", { style: emptyStyle, "data-testid": "chrono-group-empty", children: emptyState })) : null] }));
});
ChronoGroup.displayName = 'ChronoGroup';
